import { DesignPackageItem, ItemStatus, PackageStatus } from "@/services/bedrock/dtos/DesignPackage";
import type { DesignPackageProgress } from "@/utils/actioncable";

export type Stage = 1 | 2 | 3 | 4;

export const STAGES: { stage: Stage; label: string }[] = [
  { stage: 1, label: "Understanding your request" },
  { stage: 2, label: "Choosing backgrounds" },
  { stage: 3, label: "Designing layout" },
  { stage: 4, label: "Rendering previews" },
];

export const DONE_LABEL = "Your design is ready";

const STAGE_BY_STATUS: Record<PackageStatus, Stage | "done" | "failed"> = {
  queued: 1,
  generating_images: 2,
  generating_html: 3,
  rendering_previews: 4,
  completed: "done",
  failed: "failed",
};

const DEFAULT_PERCENT: Record<Stage, number> = { 1: 10, 2: 35, 3: 60, 4: 90 };

export function stageForStatus(status: PackageStatus): Stage | "done" | "failed" {
  return STAGE_BY_STATUS[status];
}

/** The stage's default percent, unless the reported percent is larger. */
export function percentFor(status: PackageStatus, percent: number | null): number {
  const stage = stageForStatus(status);
  if (stage === "done") return 100;
  if (stage === "failed") return percent ?? 0;
  const fallback = DEFAULT_PERCENT[stage];
  return percent != null && percent > fallback ? Math.min(percent, 100) : fallback;
}

/** A refinement's steps on one slide, from the item's status. */
export const REFINE_STEPS: { step: number; label: string }[] = [
  { step: 1, label: "Reading your request" },
  { step: 2, label: "Redesigning the slide" },
  { step: 3, label: "Rendering the new version" },
];

const REFINE_STEP_BY_STATUS: Partial<Record<ItemStatus, number>> = {
  queued: 1,
  generating_html: 2,
  rendering_previews: 3,
};

/**
 * Where a slide's refinement is. The cached item and the cable can each be ahead of the other (the
 * cable reports each step; a refetch lands when the new version is saved), so the later one wins.
 */
export function refineStepFor(
  item: Pick<DesignPackageItem, "id" | "status" | "progressStatus">,
  progress: DesignPackageProgress | undefined
): { step: number; sublabel: string | undefined } {
  const cached = REFINE_STEP_BY_STATUS[item.status] ?? 1;
  const live =
    progress?.designPackageItemId === item.id ? REFINE_STEP_BY_STATUS[progress.status as ItemStatus] : undefined;
  if (live != null && live >= cached) return { step: live, sublabel: progress?.progressStatus ?? undefined };
  return { step: cached, sublabel: item.progressStatus ?? undefined };
}
